import { CheckboxV2, Space, Typography } from '@ag1/ui';
import { SubscriptionLineItemV2 } from '@portal/state/internal/getSubscriptionDetails/types';
import { ChangeEvent, forwardRef, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getPrice } from 'shared/utils/customer';
import { DateFormat, useDateFormatting } from 'state/hooks/useDateFormatString';
import { useStoreCurrency } from '../../../state/hooks/useStoreCurrency';
import style from './ActiveSubscriptionCardV2.module.scss';
import { InfoItemV2 } from './InfoItemV2';
import { formatProductsListV2 } from './utils';

interface ActiveSubscriptionCardV2Props {
  subscriptionName: string;
  subscriptionProducts: SubscriptionLineItemV2[];
  oneTimeProducts?: SubscriptionLineItemV2[];
  nextOrderDate?: number;
  orderTotal?: number;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  outerChecked: boolean;
}

const ActiveSubscriptionCardV2 = forwardRef<HTMLInputElement, ActiveSubscriptionCardV2Props>(
  (
    {
      subscriptionName,
      subscriptionProducts,
      oneTimeProducts,
      nextOrderDate,
      orderTotal,
      onChange,
      outerChecked
    },
    ref
  ) => {
    const { t } = useTranslation('subscriptionDetails');

    const [innerChecked, setInnerChecked] = useState(outerChecked);
    const { symbolCurrency } = useStoreCurrency();

    const checkedCertainSubscription = (e: ChangeEvent<HTMLInputElement>) => {
      setInnerChecked(!innerChecked);
      onChange?.(e);
    };

    useEffect(() => {
      setInnerChecked(outerChecked);
    }, [outerChecked]);

    const getFormattedDate = useDateFormatting(DateFormat.Short);
    const nextOrderDateFormatted = nextOrderDate
      ? getFormattedDate(new Date(nextOrderDate * 1000).toISOString())
      : 'No Date';

    return (
      <div className={style.card}>
        <Space top={2}>
          <CheckboxV2
            ref={ref}
            id={subscriptionName}
            checked={innerChecked}
            onChange={checkedCertainSubscription}
          />
        </Space>

        <div>
          <Typography variant="portalSubtitle" size="3" color="black-new" noPadding weight="700">
            {subscriptionName}
          </Typography>

          <div className={style.infoItemList}>
            <InfoItemV2
              title={t('active.subscriptionProductsTitle')}
              value={formatProductsListV2(subscriptionProducts, t('active.qty'))}
            />
            {!!oneTimeProducts?.length && (
              <InfoItemV2
                title={t('active.addOns')}
                value={formatProductsListV2(oneTimeProducts, t('active.qty'))}
              />
            )}
            {!!nextOrderDate && (
              <InfoItemV2 title={t('active.nextOrderLabel')} value={nextOrderDateFormatted} />
            )}
            {!!orderTotal && (
              <InfoItemV2
                title={t('active.orderTotal')}
                value={`${getPrice(symbolCurrency, orderTotal)}`}
              />
            )}
          </div>
        </div>
      </div>
    );
  }
);

ActiveSubscriptionCardV2.displayName = 'ActiveSubscriptionCardV2';

export default ActiveSubscriptionCardV2;
